import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import { useCallback } from "react";
import { CESTA_BASICA_KIT } from "../../WorkSpace/CanvasViewer/EncarteConstants";

const getPriceInCents = (product) => {
  const rawPrice =
    product?.promotionPrice ?? product?.varejoPrice ?? product?.price ?? "";
  const cents = Number(String(rawPrice).replace(/\D/g, ""));
  return Number.isFinite(cents) ? cents : 0;
};

const hasInvalidCestaBasicaKitPrice = ({ format, products }) => {
  // Business rule: Kit format requires all products with price > 0 before export.
  if (format !== CESTA_BASICA_KIT) return false;
  if (!Array.isArray(products) || products.length === 0) return false;
  return products.some((product) => getPriceInCents(product) <= 0);
};

const INVALID_CESTA_BASICA_KIT_PRICE_MESSAGE =
  "Para baixar a Cesta Básica, todos os produtos precisam ter preço maior que R$ 0,00.";

export default function useCanvasDownload({
  encarteName,
  canvasScale,
  setCanvasScale,
  setCanvasSelectedProductIndex,
  canvasTargetClassName,
  format,
  products,
  canDownload,
  onBlockedDownload,
}) {
  const downloadCanvas = useCallback(async () => {
    if (!canDownload) {
      onBlockedDownload?.();
      return;
    }

    if (hasInvalidCestaBasicaKitPrice({ format, products })) {
      alert(INVALID_CESTA_BASICA_KIT_PRICE_MESSAGE);
      return;
    }

    const previousScale = canvasScale;
    setCanvasScale(3);
    setCanvasSelectedProductIndex(null);
    await new Promise((resolve) => setTimeout(resolve, 100));

    return new Promise((resolve) => {
      setTimeout(async () => {
        const upscaledCanvas = document.querySelector(
          `.${canvasTargetClassName}`,
        );
        if (!upscaledCanvas) {
          setCanvasScale(previousScale);
          resolve();
          return;
        }

        const pngCanvas = await html2canvas(upscaledCanvas, {
          scale: 0.33333,
          logging: true,
          allowTaint: true,
          useCORS: true,
        });

        const userAgent = navigator.userAgent;

        if (/iPhone/gi.test(userAgent) || /iPad/gi.test(userAgent)) {
          const blob = await new Promise((res) =>
            pngCanvas.toBlob(res, "image/jpeg", 1),
          );
          const file = new File([blob], `${encarteName || "encarte"}.jpeg`, {
            type: "image/jpeg",
          });

          try {
            if (
              navigator.share &&
              navigator.canShare &&
              navigator.canShare({ files: [file] })
            ) {
              // Native share sheet's "Save Image" is the only reliable way to reach Photos across iOS versions.
              await navigator.share({ files: [file] });
            } else {
              alert(
                "Seu navegador ou versão do iOS não suporta o download desta imagem.",
              );
            }
          } catch (err) {
            if (err?.name !== "AbortError") {
              alert(
                "Seu navegador ou versão do iOS não suporta o download desta imagem.",
              );
            }
          } finally {
            setCanvasScale(previousScale);
            resolve();
          }
        } else {
          const data = pngCanvas.toDataURL("image/jpeg", 1);
          saveAs(data, encarteName, {
            autoBom: true,
          });
          setCanvasScale(previousScale);
          await new Promise((r) => setTimeout(r, 100));
          resolve();
        }
      }, 400);
    });
  }, [
    canDownload,
    canvasScale,
    canvasTargetClassName,
    encarteName,
    format,
    onBlockedDownload,
    products,
    setCanvasScale,
    setCanvasSelectedProductIndex,
  ]);

  return { downloadCanvas };
}
